import { useMemo, useState } from 'react';
import { IconBuilding, IconClipboardCheck, IconDownload, IconHistory, IconList, IconPhoto, IconSearch, IconUpload, IconUser } from '@tabler/icons-react';
import { HeaderTool, Page, PageHeader, useLayout } from '@hana/shell';
import { Button, Skeleton, StateView, useToast } from '@hana/ui';
import { useT } from '@hana/i18n';
import { financeScreenState, useFinanceAccess } from '../finance/access';
import { norm } from '../finance/view';
import { ACC, ACCF, DOCT, PTK, PTY, TX, dayKey, f1, isDead, unmatched, waiting, type PayTab, type PayTx } from './data';
import s from './payments.module.css';
import './i18n';

// Mint-Payments (1536): sổ thu / chi / bù trừ. TIỀN → sau quyền `money`. Chỉ đọc; nút ghi báo toast. Ngăn kéo chi tiết: sau mốc desktop.

type Cf = '' | 'un' | 'wait' | 'dead';
const TABS: readonly PayTab[] = ['thu', 'chi', 'bt'];
const MS = { ok: ['pay.m.ok', s.mOk], part: ['pay.m.part', s.mPart], sug: ['pay.m.sug', s.mSug], none: ['pay.m.none', s.mNone] } as const;
const SS = { rec: 'pay.s.rec', match: 'pay.s.match', appr: 'pay.s.appr', post: 'pay.s.post', rev: 'pay.s.rev', void: 'pay.s.void' } as const;
const sgn = (x: PayTx): string => (x.tab === 'thu' ? '+' : x.tab === 'chi' ? '−' : '');
const grp = (n: number): string => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const DTS: readonly (readonly [string, string, string])[] = [['', '', 'pay.dt.all'], ['NKL', 'NKL', 'pay.dt.cus'], ['THUANPHATCM', 'THUANPHATCM', 'pay.dt.cus'], ['ONE-LINE', 'ONE-LINE', 'pay.dt.par'], ['WANHAI', 'WANHAI', 'pay.dt.par'], ['NORDICFRT', 'Nordic Freight BV', 'pay.dt.agent'], ['1/C26THL/2291', '1/C26THL/2291', 'pay.dt.inv'], ['DN26054412', 'Debit DN26054412', '']];

export function Payments() {
  const t = useT();
  const toast = useToast();
  const pad = useLayout() === 'pad';
  const access = useFinanceAccess();
  const state = financeScreenState(access);
  const [per, setPer] = useState('10');
  const [tab, setTab] = useState<PayTab>('thu');
  const [cf, setCf] = useState<Cf>('');
  const [q, setQ] = useState('');
  const [dt, setDt] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const later = () => toast({ msg: t('pay.later') });

  const nUn = TX.filter(unmatched).length, nWait = TX.filter(waiting).length, nDead = TX.filter(isDead).length;
  const rows = useMemo(() => {
    let l = state === 'empty' ? [] : TX.filter((x) => x.tab === tab);
    if (cf === 'un') l = l.filter(unmatched); else if (cf === 'wait') l = l.filter(waiting); else if (cf === 'dead') l = l.filter(isDead);
    if (dt) l = l.filter((x) => x.p === dt || x.al.some((a) => a[0] === dt));
    const n = norm(q.trim());
    if (n) l = l.filter((x) => norm(`${x.id} ${x.p} ${PTY[x.p]?.[1] ?? ''} ${x.al.map((a) => `${a[0]} ${a[2]}`).join(' ')}`).includes(n));
    return l.slice().sort((a, b) => dayKey(b.d) - dayKey(a.d) || b.at.localeCompare(a.at));
  }, [tab, cf, q, dt, state]);
  const tabN = (k: PayTab) => TX.filter((x) => x.tab === k && !isDead(x)).length;
  const tot = TX.filter((x) => x.tab === tab && !isDead(x)).reduce((a, x) => a + x.amt, 0);
  const totK = t('pay.total', { k: t(`pay.tab.${tab}`), v: f1(tot) });
  const chips: readonly (readonly [Exclude<Cf, ''>, string, number, string | undefined, string])[] = [
    ['un', t('pay.chip.un'), nUn, s.cErr, t('pay.chip.unTip')],
    ['wait', t('pay.chip.wait'), nWait, s.cWarn, t('pay.chip.waitTip')],
    ['dead', t('pay.chip.dead'), nDead, s.cDead, t('pay.chip.deadTip')],
  ];
  const colAcc = tab === 'bt' ? t('pay.col.method') : t('pay.col.account');
  const colAl = t(tab === 'thu' ? 'pay.col.alThu' : tab === 'chi' ? 'pay.col.alChi' : 'pay.col.alBt');

  return (
    <Page kind="table">
      <PageHeader
        title={t('pay.title')}
        findHint={false}
        noFind
        tabs={
          <div className={s.hTabs}>
            <div className={s.seg} role="tablist" aria-label={t('pay.period')}>
              {([['10', 10], ['09', 9]] as const).map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={per === k} onClick={() => setPer(k)}>{t('pay.month', { n: l })}</button>)}
            </div>
            {state === 'ready' && chips.map(([k, l, n, c, tip]) => (
              <button key={k} type="button" className={`${s.chip} ${c}`} aria-pressed={cf === k} aria-label={`${l} ${n}`} title={tip} onClick={() => setCf(cf === k ? '' : k)}>{l}<b>{n}</b></button>
            ))}
          </div>
        }
        actions={
          <>
            <HeaderTool label={t('pay.tool.import')} icon={IconUpload} onClick={later} />
            <HeaderTool label={t('pay.tool.export')} icon={IconDownload} onClick={later} />
            <HeaderTool label={t('pay.tool.history')} icon={IconHistory} onClick={later} />
            <Button variant="primary" icon={<IconClipboardCheck size={16} aria-hidden="true" />} onClick={later}>{t('pay.record')}</Button>
          </>
        }
      />
      <div className={pad ? s.padWrap : undefined}>
      <div className={s.page}>
        <StateView state={state} skeleton={<Skeleton rows={9} />}>
          <>
            <div className={s.bar}>
              <div className={s.seg} role="tablist" aria-label={t('pay.kind')}>
                {TABS.map((k) => <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => { setTab(k); setOpen(null); }}>{t(`pay.tab.${k}`)}<span>{tabN(k)}</span></button>)}
              </div>
              <label className={s.search}>
                <IconSearch size={15} aria-hidden="true" />
                <input aria-label={t('pay.searchLabel')} placeholder={t('pay.searchPh')} value={q} onChange={(e) => setQ(e.target.value)} />
              </label>
              <label className={s.hist}>
                <IconHistory size={15} aria-hidden="true" />
                <select aria-label={t('pay.histLabel')} value={dt} onChange={(e) => setDt(e.target.value)}>
                  {DTS.map(([v, nm, sf]) => <option key={v} value={v}>{sf === 'pay.dt.all' ? t(sf) : sf === 'pay.dt.inv' ? t(sf, { v: nm }) : sf ? `${nm} · ${t(sf)}` : nm}</option>)}
                </select>
              </label>
              <span className={s.tot}><span><IconList size={14} aria-hidden="true" />{t('pay.count', { n: rows.length })}</span><b>{totK}</b></span>
            </div>
            {pad ? (
              <section className={s.padCard} aria-label={t('pay.ledger')}>
                {rows.map((x) => <PadRow key={x.id} x={x} on={open === x.id} onOpen={() => setOpen(x.id)} />)}
                {rows.length === 0 && <div className={s.empty}>{t('pay.empty')}</div>}
              </section>
            ) : (
            <section className={s.card} aria-label={t('pay.ledger')}>
              <div className={`${s.grid} ${s.head}`} aria-hidden="true">
                <span>{t('pay.col.date')}</span><span>{colAcc}</span><span>{t('pay.col.party')}</span><span style={{ textAlign: 'right' }}>{t('pay.col.amount')}</span><span>{t('pay.col.doc')}</span><span>{colAl}</span><span>{t('pay.col.match')}</span><span />
              </div>
              <div>
                {rows.map((x) => <Row key={x.id} x={x} on={open === x.id} onOpen={() => setOpen(x.id)} />)}
                {rows.length === 0 && <div className={s.empty}>{t('pay.empty')}</div>}
              </div>
            </section>
            )}
          </>
        </StateView>
      </div>
      {pad && state === 'ready' && (
        <section className={s.sum} aria-label={t('pay.summary')}>
          <h2>{t('pay.month', { n: Number(per) })}</h2>
          {TABS.map((k) => <button key={k} type="button" onClick={() => { setTab(k); setOpen(null); }}><span>{t(`pay.tab.${k}`)}</span><b>{tabN(k)}</b></button>)}
        </section>
      )}
      </div>
    </Page>
  );
}

const HUES = [s.h0, s.h1, s.h2, s.h3, s.h4, s.h5];
const NO_PHOTO = new Set(['NHUNGTT']);
function Av({ u }: { u: string }) {
  const t = useT();
  let h = 0; for (const c of u) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const photo = !NO_PHOTO.has(u);
  const tip = t(photo ? 'pay.by' : 'pay.byNoPhoto', { u });
  return (
    <span className={`${s.av} ${photo ? `${s.photo} ${HUES[h % 6]}` : ''}`} title={tip} aria-label={tip}>
      {photo ? <svg width="26" height="26" viewBox="0 0 40 40" aria-hidden="true"><circle cx="20" cy="16" r="7.5" className={s.skin} /><path d="M5 41c1.2-9.5 7.4-13.5 15-13.5S33.8 31.5 35 41z" fill="currentColor" /></svg> : u.replace(/[^A-Z]/g, '').slice(0, 2)}
    </span>
  );
}

function Row({ x, on, onOpen }: { x: PayTx; on: boolean; onOpen: () => void }) {
  const t = useT();
  const dead = isDead(x);
  const [pt, pn] = PTY[x.p] ?? (['cus', x.p] as const);
  const m = MS[x.m];
  const fx = x.cur === 'USD' && x.fx ? `USD ${grp(x.fx[0])} · ${grp(x.fx[1])}` : '';
  const pill = dead ? ['mDead', t(SS[x.s])] as const : null;
  return (
    <button type="button" className={`${s.grid} ${s.row} ${on ? s.on : ''} ${dead ? s.dead : ''}`} aria-label={t('pay.view', { id: x.id })} onClick={onOpen}>
      <span className={s.col}><b>{x.d}</b><span>{x.at}</span></span>
      <span className={s.tx2} title={ACCF[x.acc]}>{ACC[x.acc]}</span>
      <span className={s.who} title={PTK[pt]}>{pt === 'emp' ? <IconUser size={14} aria-hidden="true" /> : <IconBuilding size={14} aria-hidden="true" />}<b>{pn}</b></span>
      <span className={s.amt}><b className={x.tab === 'thu' && !dead ? s.in : ''}>{sgn(x)}{f1(x.amt)}</b>{fx && <span>{fx}</span>}</span>
      <span className={s.doc} title={`${DOCT[x.doc[0]] ?? x.doc[0]} ${x.doc[1]}${x.doc[2] ? t('pay.withPhoto') : ''}`}>
        <i>{x.doc[0]}</i><span>{x.doc[1]}</span>{x.doc[2] === 1 && <IconPhoto size={13} aria-label={t('pay.hasPhoto')} />}
      </span>
      <span className={s.al}>
        {x.al.slice(0, 2).map((a) => <span key={a[0]} title={t('pay.allocTip', { a: a[1], b: a[0], c: a[2], v: f1(a[5]) })}><em>{a[0]}</em><small>{f1(a[5])}</small></span>)}
        {x.al.length > 2 && <span className={s.more}>+{x.al.length - 2}</span>}
        {x.al.length === 0 && <span className={s.none}>{t('pay.unalloc')}</span>}
      </span>
      <span><span className={`${s.pill} ${pill ? s.mDead : m[1]}`} title={t('pay.pillTip', { m: t(m[0]), s: t(SS[x.s]) })}><i aria-hidden="true" />{pill ? pill[1] : t(m[0])}</span></span>
      <Av u={x.by} />
    </button>
  );
}

function PadRow({ x, on, onOpen }: { x: PayTx; on: boolean; onOpen: () => void }) {
  const t = useT();
  const dead = isDead(x);
  const [pt, pn] = PTY[x.p] ?? (['cus', x.p] as const);
  const m = MS[x.m];
  return (
    <button type="button" className={`${s.padRow} ${on ? s.on : ''} ${dead ? s.dead : ''}`} aria-label={t('pay.view', { id: x.id })} onClick={onOpen}>
      <span className={s.padL1}>
        <span title={PTK[pt]}>{pt === 'emp' ? <IconUser size={15} aria-hidden="true" /> : <IconBuilding size={15} aria-hidden="true" />}</span>
        <b>{pn}</b>
        <b className={`${s.padAmt} ${x.tab === 'thu' && !dead ? s.in : ''}`}>{sgn(x)}{f1(x.amt)}</b>
      </span>
      <span className={s.padL2}>
        <span>{x.d} {x.at}</span>
        <span className={s.doc} title={`${DOCT[x.doc[0]] ?? x.doc[0]} ${x.doc[1]}`}><i>{x.doc[0]}</i><span>{x.doc[1]}</span>{x.doc[2] === 1 && <IconPhoto size={13} aria-label={t('pay.hasPhoto')} />}</span>
        <span className={s.grow} />
        <span className={`${s.pill} ${dead ? s.mDead : m[1]}`} title={t('pay.pillTip', { m: t(m[0]), s: t(SS[x.s]) })}><i aria-hidden="true" />{dead ? t(SS[x.s]) : t(m[0])}</span>
        <span className={s.av} title={t('pay.by', { u: x.by })} aria-label={t('pay.by', { u: x.by })}>{x.by.replace(/[^A-Z]/g, '').slice(0, 2)}</span>
      </span>
      <span className={s.al}>
        {x.al.slice(0, 2).map((a) => <span key={a[0]} title={t('pay.allocTip', { a: a[1], b: a[0], c: a[2], v: f1(a[5]) })}><em>{a[0]}</em><small>{f1(a[5])}</small></span>)}
        {x.al.length > 2 && <span className={s.more}>+{x.al.length - 2}</span>}
        {x.al.length === 0 && <span className={s.none}>{t('pay.unalloc')}</span>}
      </span>
    </button>
  );
}
